import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const TableStructureSvg = () => (
  <svg viewBox="0 0 80 55" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-100 ev-table-structure-svg">
    <rect x="2" y="2" width="76" height="10" rx="1.5" fill="var(--color9)" fillOpacity="0.15" stroke="var(--color14)" strokeWidth="0.5" />
    <rect x="4" y="5" width="18" height="3" rx="1" fill="var(--color9)" opacity="0.6" />
    <rect x="26" y="5" width="22" height="3" rx="1" fill="var(--color9)" opacity="0.4" />
    <rect x="52" y="5" width="14" height="3" rx="1" fill="var(--color9)" opacity="0.4" />
    <rect x="2" y="13" width="76" height="9" rx="0" stroke="var(--color14)" strokeWidth="0.3" fill="none" />
    <rect x="4" y="15.5" width="16" height="2.5" rx="0.8" fill="var(--color14)" opacity="0.5" />
    <rect x="26" y="15.5" width="20" height="2.5" rx="0.8" fill="#FFC107" opacity="0.5" />
    <rect x="52" y="15.5" width="12" height="2.5" rx="0.8" fill="#FFC107" opacity="0.5" />
    <rect x="2" y="23" width="76" height="9" rx="0" stroke="var(--color14)" strokeWidth="0.3" fill="var(--color9)" fillOpacity="0.04" />
    <rect x="4" y="25.5" width="14" height="2.5" rx="0.8" fill="var(--color14)" opacity="0.5" />
    <rect x="26" y="25.5" width="18" height="2.5" rx="0.8" fill="#FFC107" opacity="0.5" />
    <rect x="52" y="25.5" width="10" height="2.5" rx="0.8" fill="#FFC107" opacity="0.5" />
    <rect x="2" y="33" width="76" height="9" rx="0" stroke="var(--color14)" strokeWidth="0.3" fill="none" />
    <rect x="4" y="35.5" width="18" height="2.5" rx="0.8" fill="var(--color14)" opacity="0.5" />
    <rect x="26" y="35.5" width="15" height="2.5" rx="0.8" fill="#FFC107" opacity="0.5" />
    <rect x="52" y="35.5" width="13" height="2.5" rx="0.8" fill="#FFC107" opacity="0.5" />
    <rect x="2" y="43" width="76" height="9" rx="0" stroke="var(--color14)" strokeWidth="0.3" fill="var(--color9)" fillOpacity="0.04" />
    <rect x="4" y="45.5" width="12" height="2.5" rx="0.8" fill="var(--color14)" opacity="0.5" />
    <rect x="26" y="45.5" width="20" height="2.5" rx="0.8" fill="#FFC107" opacity="0.5" />
    <rect x="52" y="45.5" width="11" height="2.5" rx="0.8" fill="#FFC107" opacity="0.5" />
  </svg>
);

const TableStructureCard = ({ structure, isSelected, onSelect, onPreview }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="col-6 position-relative"
      onClick={() => onSelect(structure.id)}
      role="button"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={`ev-card-2 ev-radius-small ev-padding-xsmall d-flex flex-column align-items-center justify-content-center position-relative ev-table-structure-card${
          isSelected ? " active-content-style" : ""
        }`}
      >
        <TableStructureSvg />

        {hovered && (
          <button
            type="button"
            className="ev-table-structure-eye-btn d-flex align-items-center justify-content-center"
            onClick={(e) => onPreview(e, structure)}
            title="Preview table structure"
          >
            <i className="bi bi-eye ev-fs-11"></i>
          </button>
        )}

        <span
          className={`ev-message-card1 py-0 px-1 mt-1 mb-0 w-100 text-center d-block text-truncate${
            isSelected
              ? " ev-bold-med ev-xxsmall-p-blue"
              : " ev-xxsmall-p-blue"
          }`}
          title={structure.structure_name}
        >
          {structure.structure_name}
        </span>
      </div>
    </div>
  );
};

const TableStructureSelector = ({
  sectionKey,
  agentKey,
  selectedId,
  onSelect,
  disabled = false,
  structures = [],
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [previewModal, setPreviewModal] = useState(null);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const openPreview = (e, structure) => {
    e.stopPropagation();
    setPreviewModal({
      id: structure.id,
      name: structure.structure_name,
      html: structure.html || "",
    });
  };

  const handleSelect = (id) => {
    onSelect(id);
    setIsOpen(false);
  };

  const preparePreviewHtml = (html) => {
    if (!html) return "";
    const highlighted = html.replace(
      /\[GENERATE_HERE\]/g,
      '<span class="ev-table-structure-placeholder">[GENERATE_HERE]</span>'
    );
    return highlighted;
  };

  const filteredStructures = structures.filter((ts) =>
    ts.structure_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`ev-btn-text-only-fill ev-radius-xsmall ev-icon-btn-padding-small avatar-tooltip ${selectedId ? "ev-bold-med" : ""}`}
        title="Table Structure"
        disabled={disabled}
        data-tooltip="Table Structure"
        onClick={() => { setIsOpen((prev) => !prev); setSearchTerm(""); }}
      >
        <i className={`bi bi-grid-3x3-gap-fill ev-fs-16 ${selectedId ? "text-primary" : ""}`}></i>
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="ev-table-structure-dropdown ev-card-4 ev-radius-small position-absolute d-flex flex-column"
        >
          <span className="ev-bold-med d-block ev-fs-13 text-center mb-2 ev-med-p-blue">
            Table Structure
          </span>

          {loading ? (
            <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1">
              <span className="spinner-border text-primary mb-2" style={{ width: '1.5rem', height: '1.5rem' }}></span>
              <span className="ev-fs-12 ev-med-p-blue">Loading...</span>
            </div>
          ) : structures.length === 0 ? (
            <p className="ev-message-card1 ev-fs-12 text-center mb-0 py-2">No table structures available</p>
          ) : (
            <>
              <div className="genbi-mention-searchwrap position-relative mb-2 px-1">
                <input
                  type="text"
                  className="form-control search-input ms-0"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  className="ev-btn-lightblue-fill-round search-bar-button"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="bi bi-search"></i>
                </button>
              </div>

              <div className="row g-2 px-1 mb-0 scrollable-element">
                {!searchTerm && (
                <div
                  className="col-6"
                  onClick={() => handleSelect(null)}
                  role="button"
                >
                  <div
                    className={`ev-card-2 ev-radius-small ev-padding-xsmall d-flex flex-column align-items-center justify-content-center ev-table-structure-card${
                      selectedId == null ? " active-content-style" : ""
                    }`}
                  >
                    <svg viewBox="0 0 80 55" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-100 ev-table-structure-svg">
                      <rect x="26" y="12" width="28" height="3" rx="1.5" fill="#dc3545" opacity="0.7" />
                      <rect x="34" y="9" width="12" height="4" rx="1" stroke="#dc3545" strokeWidth="1" fill="none" opacity="0.6" />
                      <path d="M28 17 L30 44 L50 44 L52 17 Z" stroke="#dc3545" strokeWidth="1.2" fill="#dc3545" fillOpacity="0.08" strokeLinejoin="round" />
                      <line x1="35" y1="21" x2="35" y2="40" stroke="#dc3545" strokeWidth="1" opacity="0.4" strokeLinecap="round" />
                      <line x1="40" y1="21" x2="40" y2="40" stroke="#dc3545" strokeWidth="1" opacity="0.4" strokeLinecap="round" />
                      <line x1="45" y1="21" x2="45" y2="40" stroke="#dc3545" strokeWidth="1" opacity="0.4" strokeLinecap="round" />
                    </svg>
                    <span
                      className={`ev-message-card1 py-0 px-2 mt-1 mb-0 w-100 text-center d-block${
                        selectedId == null
                          ? " ev-bold-med ev-xxsmall-p-blue"
                          : " ev-xxsmall-p-blue"
                      }`}
                    >
                      None
                    </span>
                  </div>
                </div>
                )}

                {filteredStructures.map((ts) => {
                  const isSelected = selectedId === ts.id;
                  return (
                    <TableStructureCard
                      key={ts.id}
                      structure={ts}
                      isSelected={isSelected}
                      onSelect={handleSelect}
                      onPreview={openPreview}
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {previewModal &&
        createPortal(
          <div
            className="ev-table-structure-preview-overlay"
            onClick={() => setPreviewModal(null)}
          >
            <div
              className="ev-table-structure-preview-modal ev-card-4 ev-radius-small d-flex flex-column overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex align-items-center justify-content-between pb-2 border-bottom mb-2">
                <h6 className="ev-med-p-blue mb-0">
                  <i className="bi bi-table me-2"></i>
                  {previewModal.name}
                </h6>
                <button
                  type="button"
                  className="ev-btn-text-only-fill ev-fs-20"
                  onClick={() => setPreviewModal(null)}
                >
                  <i className="bi bi-x"></i>
                </button>
              </div>
              <div
                className="ev-card-2 ev-radius-small p-3 overflow-auto scrollable-element ev-table-structure-preview-body flex-grow-1 ev-reg-p-dark"
                dangerouslySetInnerHTML={{
                  __html: preparePreviewHtml(previewModal.html),
                }}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default TableStructureSelector;